import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { type AdminTheme, readStoredTheme } from '@/lib/adminTheme';

type ThemeProviderProps = {
  children: ReactNode;
  defaultTheme?: AdminTheme;
  storageKey: string;
};

type ThemeProviderState = {
  theme: AdminTheme;
  setTheme: (theme: AdminTheme) => void;
};

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(
  undefined,
);

export const ThemeProvider = ({
  children,
  defaultTheme = 'light',
  storageKey,
}: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<AdminTheme>(
    () => readStoredTheme(storageKey) ?? defaultTheme,
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const setTheme = (nextTheme: AdminTheme) => {
    window.localStorage.setItem(storageKey, nextTheme);
    setThemeState(nextTheme);
  };

  return (
    <ThemeProviderContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
